const LEGACY_STORAGE_KEYS = {
  patient: "afrimed.notifications.patient",
  doctor: "afrimed.notifications.doctor",
  pharmacy: "afrimed.notifications.pharmacy",
  admin: "afrimed.notifications.admin",
};

export const NOTIFICATION_PREFS_CHANGED_EVENT =
  "afrimed:notification-prefs:changed";
export const MESSAGE_ALERT_SOUND_OPTIONS = ["off", "soft", "standard", "urgent"];
export const DEFAULT_MESSAGE_ALERT_SOUND = "standard";

function normalizeMessageAlertSound(value, fallback = DEFAULT_MESSAGE_ALERT_SOUND) {
  const sound = String(value || "").trim().toLowerCase();
  return MESSAGE_ALERT_SOUND_OPTIONS.includes(sound) ? sound : fallback;
}

function canUseBrowserNotifications() {
  return typeof window !== "undefined" && "Notification" in window;
}

function canUseVibration() {
  try {
    return typeof window !== "undefined" && "vibrate" in navigator;
  } catch {
    return false;
  }
}

export function getBrowserNotificationPermission() {
  if (!canUseBrowserNotifications()) return "unsupported";
  return String(window.Notification?.permission || "default").trim() || "default";
}

export async function requestBrowserNotificationPermission() {
  if (!canUseBrowserNotifications()) return "unsupported";
  try {
    return await window.Notification.requestPermission();
  } catch {
    return getBrowserNotificationPermission();
  }
}

function resolveScopedStorageKey(role = "patient", userId = "") {
  const cleanRole = String(role || "patient").trim().toLowerCase() || "patient";
  const cleanUserId = String(userId || "guest").trim() || "guest";
  return `afrimed.notifications.${cleanRole}:${cleanUserId}`;
}

function resolveLegacyStorageKey(role = "patient") {
  const cleanRole = String(role || "patient").trim().toLowerCase() || "patient";
  return LEGACY_STORAGE_KEYS[cleanRole] || LEGACY_STORAGE_KEYS.patient;
}

export function getNotificationPrefsDefaults(role = "patient") {
  const browserPushOn = getBrowserNotificationPermission() === "granted";
  return {
    appointmentRemindersOn: true,
    inAppOn: true,
    emailOn: true,
    smsOn: false,
    browserPushOn,
    soundOn: true,
    vibrationOn: canUseVibration(),
    messageAlertSound: DEFAULT_MESSAGE_ALERT_SOUND,
  };
}

export function readNotificationPrefsCache(role = "patient", userId = "") {
  if (typeof window === "undefined") return {};

  const scopedKey = resolveScopedStorageKey(role, userId);
  const legacyKey = resolveLegacyStorageKey(role);

  try {
    const raw =
      window.localStorage.getItem(scopedKey) ||
      window.localStorage.getItem(legacyKey) ||
      "";
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function writeNotificationPrefsCache(role = "patient", userId = "", next = {}) {
  if (typeof window === "undefined") return;

  const scopedKey = resolveScopedStorageKey(role, userId);
  const legacyKey = resolveLegacyStorageKey(role);
  const payload = JSON.stringify(next && typeof next === "object" ? next : {});

  try {
    window.localStorage.setItem(scopedKey, payload);
    window.localStorage.setItem(legacyKey, payload);
    window.dispatchEvent(
      new CustomEvent(NOTIFICATION_PREFS_CHANGED_EVENT, {
        detail: { role, userId },
      })
    );
  } catch {
    // ignore local storage failures
  }
}

export function mapDbPrefsToUi(raw, role = "patient") {
  const obj = raw && typeof raw === "object" ? raw : {};
  const hasFlatUiShape =
    "appointmentRemindersOn" in obj ||
    "inAppOn" in obj ||
    "emailOn" in obj ||
    "smsOn" in obj ||
    "browserPushOn" in obj ||
    "soundOn" in obj ||
    "vibrationOn" in obj ||
    "messageAlertSound" in obj;
  if (hasFlatUiShape) {
    const defaults = getNotificationPrefsDefaults(role);
    const soundOn =
      typeof obj.soundOn === "boolean" ? obj.soundOn : defaults.soundOn;
    const messageAlertSound = soundOn
      ? normalizeMessageAlertSound(obj.messageAlertSound, defaults.messageAlertSound)
      : "off";
    return {
      appointmentRemindersOn:
        typeof obj.appointmentRemindersOn === "boolean"
          ? obj.appointmentRemindersOn
          : defaults.appointmentRemindersOn,
      inAppOn:
        typeof obj.inAppOn === "boolean" ? obj.inAppOn : defaults.inAppOn,
      emailOn:
        typeof obj.emailOn === "boolean" ? obj.emailOn : defaults.emailOn,
      smsOn: typeof obj.smsOn === "boolean" ? obj.smsOn : defaults.smsOn,
      browserPushOn:
        typeof obj.browserPushOn === "boolean"
          ? obj.browserPushOn
          : defaults.browserPushOn,
      soundOn,
      vibrationOn:
        typeof obj.vibrationOn === "boolean"
          ? obj.vibrationOn
          : defaults.vibrationOn,
      messageAlertSound,
    };
  }

  const reminderPrefs =
    obj?.appointment_reminders && typeof obj.appointment_reminders === "object"
      ? obj.appointment_reminders
      : {};
  const reminderChannels =
    reminderPrefs?.channels && typeof reminderPrefs.channels === "object"
      ? reminderPrefs.channels
      : {};
  const devicePrefs =
    obj?.device_alerts && typeof obj.device_alerts === "object"
      ? obj.device_alerts
      : {};
  const deviceChannels =
    devicePrefs?.channels && typeof devicePrefs.channels === "object"
      ? devicePrefs.channels
      : {};
  const messageAlertPrefs =
    devicePrefs?.message_alerts && typeof devicePrefs.message_alerts === "object"
      ? devicePrefs.message_alerts
      : {};
  const defaults = getNotificationPrefsDefaults(role);
  const soundOn =
    typeof deviceChannels?.sound === "boolean"
      ? deviceChannels.sound
      : defaults.soundOn;
  const messageAlertSound = soundOn
    ? normalizeMessageAlertSound(
        messageAlertPrefs?.sound_profile,
        defaults.messageAlertSound
      )
    : "off";

  return {
    appointmentRemindersOn:
      typeof reminderPrefs?.enabled === "boolean"
        ? reminderPrefs.enabled
        : defaults.appointmentRemindersOn,
    inAppOn:
      typeof deviceChannels?.in_app === "boolean"
        ? deviceChannels.in_app
        : typeof reminderChannels?.in_app === "boolean"
          ? reminderChannels.in_app
        : defaults.inAppOn,
    emailOn:
      typeof reminderChannels?.email === "boolean"
        ? reminderChannels.email
        : defaults.emailOn,
    smsOn:
      typeof reminderChannels?.sms === "boolean"
        ? reminderChannels.sms
        : defaults.smsOn,
    browserPushOn:
      typeof deviceChannels?.push === "boolean"
        ? deviceChannels.push
        : defaults.browserPushOn,
    soundOn,
    vibrationOn:
      typeof deviceChannels?.vibration === "boolean"
        ? deviceChannels.vibration
        : defaults.vibrationOn,
    messageAlertSound,
  };
}

export function mapUiPrefsToDb(prefs = {}, role = "patient") {
  const defaults = getNotificationPrefsDefaults(role);
  const appointmentRemindersOn =
    typeof prefs?.appointmentRemindersOn === "boolean"
      ? prefs.appointmentRemindersOn
      : defaults.appointmentRemindersOn;
  const inAppOn = Boolean(prefs?.inAppOn);
  const emailOn = Boolean(prefs?.emailOn);
  const smsOn = false;
  const browserPushOn = Boolean(prefs?.browserPushOn);
  const requestedMessageAlertSound = normalizeMessageAlertSound(
    prefs?.messageAlertSound,
    defaults.messageAlertSound
  );
  const soundOn =
    typeof prefs?.soundOn === "boolean"
      ? prefs.soundOn && requestedMessageAlertSound !== "off"
      : requestedMessageAlertSound !== "off";
  const vibrationOn = Boolean(prefs?.vibrationOn);
  const messageAlertSound = soundOn
    ? normalizeMessageAlertSound(requestedMessageAlertSound, defaults.messageAlertSound)
    : "off";
  // Appointment reminders have one visible, role-neutral schedule.  The worker
  // owns the evaluation window; this persisted value documents the exact
  // product contract that the Notifications controls enable.
  const timings = { hours_before: [24, 2, 1, 0.5] };

  return {
    appointment_reminders: {
      enabled: appointmentRemindersOn,
      channels: {
        in_app: appointmentRemindersOn && inAppOn,
        email: emailOn,
        sms: smsOn,
        push: appointmentRemindersOn && browserPushOn,
      },
      timings,
    },
    device_alerts: {
      enabled: browserPushOn || soundOn || vibrationOn,
      channels: {
        in_app: inAppOn,
        push: browserPushOn,
        sound: soundOn,
        vibration: vibrationOn,
      },
      message_alerts: {
        sound_profile: messageAlertSound,
      },
      quiet_hours: null,
    },
    updated_at: new Date().toISOString(),
  };
}

// These are deliberately scoped fragments, rather than a full preference
// document. Settings surfaces must only persist the field the person changed;
// hydration and unrelated device-alert changes must never replace appointment
// reminder eligibility.
export function buildAppointmentReminderPatch(prefs = {}, role = "patient") {
  return mapUiPrefsToDb(prefs, role).appointment_reminders;
}

export function buildDeviceAlertsPatch(prefs = {}, role = "patient") {
  return mapUiPrefsToDb(prefs, role).device_alerts;
}

// A missing appointment-reminder preference is distinct from an explicit opt-out.
// Booking may establish the product default only in the former case.
export function hasExplicitAppointmentReminderChoice(raw = {}) {
  const prefs = raw && typeof raw === "object" ? raw : {};
  if (typeof prefs.appointmentRemindersOn === "boolean") return true;
  return typeof prefs?.appointment_reminders?.enabled === "boolean";
}

export function applyDefaultAppointmentReminderPreference(raw = {}, role = "patient") {
  const current = raw && typeof raw === "object" ? raw : {};
  if (hasExplicitAppointmentReminderChoice(current)) {
    return { changed: false, notificationPrefs: current };
  }

  const defaulted = mapUiPrefsToDb(
    { ...mapDbPrefsToUi(current, role), appointmentRemindersOn: true },
    role,
  );
  return {
    changed: true,
    appointmentReminderPatch: defaulted.appointment_reminders,
    notificationPrefs: { ...current, ...defaulted },
  };
}

export function resolveDeviceAlertPrefs(raw, role = "patient") {
  const merged = mapDbPrefsToUi(raw, role);
  return {
    inAppOn: Boolean(merged.inAppOn),
    browserPushOn: Boolean(merged.browserPushOn),
    soundOn: Boolean(merged.soundOn) && merged.messageAlertSound !== "off",
    vibrationOn: Boolean(merged.vibrationOn),
    messageAlertSound: normalizeMessageAlertSound(
      merged.messageAlertSound,
      DEFAULT_MESSAGE_ALERT_SOUND
    ),
  };
}
